import { z } from 'zod';

import { type AppliedFilters, filterText } from '@/components/FilterBar/filters';
import { LOOKBOOK_ACTIVE, LOOKBOOK_FAMILY } from '@/features/lookbooks/family';
import { LOOKBOOK_FILTER_KEYS, type LookbookFilterKey } from '@/features/lookbooks/lookbookFilters';

/*
  The register's URL contract: the search, the applied filters and the page. The page's only
  state for these is the URL, so a filtered link restores exactly that view.

  Every key is optional and `.catch(undefined)`, so a mangled link opens the opening view rather
  than throwing the route. Ids travel as numbers, so the URL reads category=4 and matches the
  API's categoryId=4; the neutral choice ('All') and page 1 are never written.

  Active is the one exception (ALS-289). The URL carries what differs from the opening view, and
  the register opens on Active: Yes, so a plain link opens there, `active=All` is what a link
  showing every book carries, and Yes itself is never written.
*/

/** A range as the drawer writes it: either day may be missing, but not both (`2026-01-01..`). */
const DAY_RANGE = /^(?=.*\d)(\d{4}-\d{2}-\d{2})?\.\.(\d{4}-\d{2}-\d{2})?$/;

/** An id into a register: a positive whole number, or nothing. */
const idParam = z.coerce.number().int().positive().optional().catch(undefined);
const rangeParam = z.string().regex(DAY_RANGE).optional().catch(undefined);

export const lookbooksSearch = z.object({
    // The router parses a numeric-looking value as a number, so a search for "2026" is kept.
    q: z.union([z.string(), z.number()]).transform(String).optional().catch(undefined),
    page: z.coerce.number().int().min(1).optional().catch(undefined),
    type: z
        .enum([
            LOOKBOOK_FAMILY.pages,
            LOOKBOOK_FAMILY.photos,
            LOOKBOOK_FAMILY.video,
            LOOKBOOK_FAMILY.products,
        ])
        .optional()
        .catch(undefined),
    category: idParam,
    client: idParam,
    colour: idParam,
    active: z
        .enum([LOOKBOOK_ACTIVE.all, LOOKBOOK_ACTIVE.yes, LOOKBOOK_ACTIVE.no])
        .optional()
        .catch(undefined),
    tag: idParam,
    createdBy: idParam,
    revisedBy: idParam,
    effective: rangeParam,
});

/** The search as the URL carries it, after the schema. */
export type RawLookbooksSearch = z.infer<typeof lookbooksSearch>;

/** The search as the page reads it. */
export interface LookbooksSearch {
    q: string;
    filters: AppliedFilters<LookbookFilterKey>;
    page: number;
}

const K = LOOKBOOK_FILTER_KEYS;

/** The filters an id stands for, in the URL's key order. */
const ID_KEYS = [K.category, K.client, K.colour, K.tag, K.createdBy, K.revisedBy] as const;

/** The URL's search as the page reads it. */
export function readLookbooksSearch(raw: RawLookbooksSearch): LookbooksSearch {
    const filters: AppliedFilters<LookbookFilterKey> = {};
    if (raw.type) filters.type = raw.type;
    for (const key of ID_KEYS) {
        const value = raw[key];
        if (value !== undefined) filters[key] = String(value);
    }
    // No Active in the URL is the opening view's Yes; All is the neutral, so it stays out of the set.
    const active = raw.active ?? LOOKBOOK_ACTIVE.yes;
    if (active !== LOOKBOOK_ACTIVE.all) filters.active = active;
    if (raw.effective) filters.effective = raw.effective;
    return { q: raw.q ?? '', filters, page: raw.page ?? 1 };
}

/** The page's search as the URL writes it: neutral values, the opening Active: Yes and page 1 left out. */
export function writeLookbooksSearch(search: LookbooksSearch): RawLookbooksSearch {
    // Through the schema, so what is written is exactly what would be read back: a junk value
    // or the neutral 'All' simply drops out.
    const { filters } = search;
    const active = filterText(filters, K.active);
    return lookbooksSearch.parse({
        q: search.q.trim() || undefined,
        page: search.page > 1 ? search.page : undefined,
        type: filterText(filters, K.type),
        category: numeric(filterText(filters, K.category)),
        client: numeric(filterText(filters, K.client)),
        colour: numeric(filterText(filters, K.colour)),
        // Yes is the opening view; no Active at all is All, written so a shared link keeps it.
        active:
            active === LOOKBOOK_ACTIVE.yes
                ? undefined
                : active === LOOKBOOK_ACTIVE.no
                  ? LOOKBOOK_ACTIVE.no
                  : LOOKBOOK_ACTIVE.all,
        tag: numeric(filterText(filters, K.tag)),
        createdBy: numeric(filterText(filters, K.createdBy)),
        revisedBy: numeric(filterText(filters, K.revisedBy)),
        effective: filterText(filters, K.effective),
    });
}

function numeric(value: string | undefined): number | undefined {
    return value !== undefined && /^\d+$/.test(value) ? Number(value) : undefined;
}
